import React from "react";
import { fetchQuery, graphql } from "react-relay";
import type { MenuProps } from "react-select";

import { debounce } from "lodash";

import type { Contact } from "~/common/EmailListInput";
import { str2AttendeeColors } from "~/lib/utils";
import { environment } from "~/models";
import { Checkbox, Tooltip, colors as uiColors } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";
import AsyncMultiSelector, {
	type AsyncMultiSelectorProps,
	components,
	type GroupBase,
	type MultiSelectorOption,
	type MultiValue as MultiValueType,
	type OptionProps,
	type SelectComponentsConfig,
} from "~/ui/selectors/AsyncMultiSelector";

import { loadOptions, type QueryOptions, type UserSelectorValue } from "./UserSelector";

import type { UserMultiSelectorQuery } from "~/__generated__/UserMultiSelectorQuery.graphql";

import styles from "./UserMultiSelector.module.css";

export const defaultLoadOptions = ({
	excludeUserIDs,
	excludeGuests,
	ranked,
	excludeManagedUsers,
	excludeAncestryFor,
}: QueryOptions) =>
	fetchQuery<UserMultiSelectorQuery>(
		environment,
		graphql`
			query UserMultiSelectorQuery(
				$search: String
				$isGuests: Boolean
				$ranked: Boolean
				$exclude: [ID]
				$excludeAncestryFor: ID
				$excludeManagedUsers: Boolean
			) {
				viewer {
					selectUsers: users(
						first: 25
						search: $search
						isGuests: $isGuests
						ranked: $ranked
						exclude: $exclude
						excludeAncestryFor: $excludeAncestryFor
						excludeManagedUsers: $excludeManagedUsers
					) {
						edges {
							node {
								id
								email
								fullName
								isGuest
								image
								internalAttributes {
									manager {
										id
										fullName
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			isGuests: excludeGuests ? false : undefined,
			ranked,
			exclude: excludeUserIDs,
			excludeAncestryFor: excludeAncestryFor,
			excludeManagedUsers,
		},
	).toPromise();

export interface UserMultiSelectorOption extends MultiSelectorOption {
	readonly avatar?: UserSelectorValue;
	readonly email?: string;
}

const Option = ({ children, data, innerProps, ...rest }: OptionProps<UserMultiSelectorOption, true>) => {
	const { isSelected } = rest;

	const avatar = data["avatar"];

	return (
		<components.Option {...rest} data={data} innerProps={innerProps}>
			<Checkbox checked={isSelected} disabled={data.isDisabled} />
			{avatar && <Avatar user={avatar} width="16px" className={styles.avatarOption} />}
			<span className={styles.optionText}>{children}</span>
		</components.Option>
	);
};

const MenuHOC = (hasItems: boolean, clearItems: () => void) => {
	function MenuInside({ children, ...rest }: MenuProps<UserMultiSelectorOption, true>) {
		return (
			<components.Menu {...rest}>
				{children}
				{hasItems && (
					<div onClick={clearItems} className={styles.clearItems}>
						Clear list
					</div>
				)}
			</components.Menu>
		);
	}

	return MenuInside;
};

interface UserMultiSelectorProps extends QueryOptions, AsyncMultiSelectorProps<MultiSelectorOption> {
	defaultUsers?: readonly UserMultiSelectorOption[];
	excludeUserIDs?: string[];
	placeholder?: React.ReactNode;
	selectedUsers: readonly UserMultiSelectorOption[];
	lockedUsers?: readonly UserMultiSelectorOption[];
	textOnly?: boolean;
	onChange?: (newUser: MultiValueType<UserMultiSelectorOption>) => void;
}

function SimpleAttendee({ user }: { user: Contact }) {
	const { fullName, email } = user;

	const userName = fullName || email || "";
	const colors = str2AttendeeColors(userName);

	const styleColors = {
		"--attendee-ring-color": `#${colors.ring}`,
	};

	return (
		<Tooltip content={userName} placement="bottom">
			<div className={styles.avatar} style={{ ...styleColors, height: "16px" }}>
				<Avatar user={user} width="16px" link={false} useTitle={false} />
			</div>
		</Tooltip>
	);
}

function UserTextDisplay({ users }: { users: readonly UserMultiSelectorOption[] }) {
	const textContent = React.useMemo(() => {
		return users
			.filter(u => u.avatar)
			.map(u => u.avatar?.fullName || u.avatar?.email || "")
			.join(", ");
	}, [users]);

	return <span>{textContent}</span>;
}

function UserAvatarDisplay({ users }: { users: readonly UserMultiSelectorOption[] }) {
	const contactUsers = React.useMemo(() => users.filter(u => u.avatar).map(u => u.avatar as Contact), [users]);

	return <SimpleAttendeesList users={contactUsers} />;
}

export function SimpleAttendeesList({ users }: { users: Contact[] }) {
	const numAttendees = 8;

	const firstUsers = users.slice(0, numAttendees).reverse();
	const othersUsers = users.slice(numAttendees);
	const othersLength = othersUsers.length;

	const othersAvatar = (
		<div className={styles.avatar} style={{ height: "16px" }}>
			<Avatar
				width="16px"
				link={false}
				useTitle={false}
				uiAvatarOptions={{ background: uiColors.grey[200].substring(1), color: uiColors.grey[600].substring(1) }}
				initials={othersLength}
			/>
		</div>
	);

	return (
		<div className={styles.avatarsWrapper}>
			{firstUsers.map(u => (
				<SimpleAttendee key={u.id} user={u} />
			))}
			{othersLength > 0 && (othersLength === 1 ? <SimpleAttendee user={othersUsers[0]} /> : othersAvatar)}
		</div>
	);
}

export default function UserMultiSelector({
	defaultUsers,
	excludeUserIDs,
	excludeGuests,
	excludeManagedUsers,
	excludeAncestryFor,
	lockedUsers = [],
	placeholder,
	ranked,
	selectedUsers: selectedUsersProp,
	textOnly,
	onChange,
	...rest
}: UserMultiSelectorProps) {
	// Merge locked users with selected users
	const selectedUsers = React.useMemo(() => {
		if (lockedUsers.length === 0) return selectedUsersProp;
		if (selectedUsersProp.length === 0) return lockedUsers;

		const selectedIds = new Set(selectedUsersProp.map(user => user.value));
		const missingLockedUsers = lockedUsers.filter(user => !selectedIds.has(user.value));

		return [...selectedUsersProp, ...missingLockedUsers];
	}, [selectedUsersProp, lockedUsers]);

	const customComponents:
		| SelectComponentsConfig<UserMultiSelectorOption, true, GroupBase<UserMultiSelectorOption>>
		| undefined = {};

	const clearItems = React.useCallback(() => {
		if (onChange) {
			// Only keep locked users when clearing
			onChange(lockedUsers);
		}
	}, [onChange, lockedUsers]);

	const handleChange = React.useCallback(
		(newUsers: MultiValueType<UserMultiSelectorOption>) => {
			if (!onChange) return;
			if (lockedUsers.length === 0) {
				onChange(newUsers);
				return;
			}

			// Ensure locked users are always included
			const newUserIds = new Set(newUsers.map(user => user.value));
			const missingLockedUsers = lockedUsers.filter(user => !newUserIds.has(user.value));

			onChange([...newUsers, ...missingLockedUsers]);
		},
		[onChange, lockedUsers],
	);

	const lockedUserIds = React.useMemo(() => new Set(lockedUsers.map(user => user.value)), [lockedUsers]);

	const OptionWithLocked = React.useCallback(
		(props: OptionProps<UserMultiSelectorOption, true>) => {
			const isDisabled = lockedUserIds.has(props.data.value);
			return <Option {...props} data={{ ...props.data, isDisabled }} />;
		},
		[lockedUserIds],
	);

	customComponents.Option = OptionWithLocked;
	customComponents.Menu = MenuHOC(selectedUsers.length > 0, clearItems);

	const usersValue = React.useMemo(() => {
		return textOnly ? <UserTextDisplay users={selectedUsers} /> : <UserAvatarDisplay users={selectedUsers} />;
	}, [selectedUsers, textOnly]);

	const debounceLoadOptions = React.useMemo(
		() =>
			debounce(loadOptions({ excludeUserIDs, excludeGuests, ranked, excludeManagedUsers, excludeAncestryFor }), 300),
		[excludeUserIDs, excludeGuests, ranked, excludeManagedUsers, excludeAncestryFor],
	);

	const defaultUsersPlusUniqueSelectedUsers = React.useMemo(() => {
		const selectedValues = new Set(selectedUsers.map(selectedUser => selectedUser.value));
		const filteredDefaultUsers = defaultUsers ? defaultUsers.filter(dU => !selectedValues.has(dU.value)) : [];
		const result = selectedUsers.concat(filteredDefaultUsers);
		return result.length > 0 ? result : undefined;
	}, [defaultUsers, selectedUsers]);

	return (
		<AsyncMultiSelector
			components={customComponents}
			defaultOptions={defaultUsersPlusUniqueSelectedUsers}
			loadOptions={debounceLoadOptions}
			placeholder={placeholder}
			value={selectedUsers}
			onChange={handleChange}
			valueComponent={usersValue}
			{...rest}
		/>
	);
}

export type { MultiValueType };
